import React, {useState} from "react";
import {Link, useLocation} from "wouter";
import {
    Moon,
    Sun,
    Menu,
    X,
    LogOut,
    Star,
    Gift,
    User,
    LayoutDashboard,
} from "lucide-react";
import {Button} from "../ui/button";
import AuthModal from "../auth/AuthModal";
import {useDarkMode} from "../../hooks/use-dark-mode";
import {useAuth} from "../../context/AuthContext";
import logo from "../../assets/logo.png";
import VerifyCustomerModal from "../booking/VerifyCustomerModal";
import {checkBooking} from "../../api/api";
import {Avatar, AvatarFallback} from "../ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import CheckBookingVerificationModal, {Booking} from "../booking/CheckBookingVerificationModal";
import BellDropdown from "../common/BellDropdown";

const Header = () => {
    const [location] = useLocation();
    const {isDarkMode, toggle} = useDarkMode();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
    const [authType, setAuthType] = useState<"login" | "register">("login");
    const {user, isAuthenticated, logoutUser} = useAuth();
    const [verificationBooking, setVerificationBooking] = useState<Booking | null>(null);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);

    const [, navigate] = useLocation();

    const getInitials = (name: string = "") =>
        name
            .split(" ")
            .map((n) => n[0]?.toUpperCase())
            .slice(0, 2)
            .join("");

    const handleLogout = () => logoutUser();
    const toggleMobileMenu = () => setIsMobileMenuOpen((s) => !s);
    const openAuthModal = (type: "login" | "register") => {
        setAuthType(type);
        setIsAuthModalOpen(true);
    };
    const closeAuthModal = () => setIsAuthModalOpen(false);

    const handleVerifyCustomer = async (code: string) => {
        setIsVerifying(true);           // Start loading
        try {
            const booking = await checkBooking(code);
            if (booking) {
                setVerificationBooking(booking);
                setShowVerificationModal(true);
            }
            setIsVerifyModalOpen(false);
        } catch (error) {
            // handle error (optional: show error message)
        } finally {
            setIsVerifying(false);      // End loading
        }
    };

    const navLinks = [
        {name: "Home", path: "/"},
        {name: "Explore", path: "/explore"},
        {name: "How It Works", path: "/how-it-works"},
        {name: "Refer & Earn", path: "/referral"},
    ];

    return (
        <>
            <nav className="sticky top-0 z-50 bg-white py-4 shadow-sm transition-colors duration-200 dark:bg-gray-800">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between">
                        {/* Logo */}
                        <Link href="/" className="flex items-center space-x-2">
                            <img src={logo} alt="Logo" className="h-10 w-auto"/>
                            <span className="font-serif text-2xl font-bold text-primary dark:text-primary">
                                SurpriseTable
                            </span>
                        </Link>

                        {/* Desktop nav */}
                        <div className="hidden md:flex items-center space-x-6">
                            {navLinks.map((link) => (
                                <Link key={link.path} href={link.path}>
                                    <span
                                        className={`cursor-pointer transition-colors ${
                                            location === link.path
                                                ? "text-primary dark:text-primary"
                                                : "text-gray-700 dark:text-gray-200 hover:text-primary dark:hover:text-primary"
                                        }`}
                                    >
                                        {link.name}
                                    </span>
                                </Link>
                            ))}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center space-x-2">
                            {/* Dark mode toggle: always visible */}
                            {user?.is_restaurant_owner ? (
                                <div className="flex items-center gap-2">
                                    <Button
                                        className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-green-700 focus:ring-2 focus:ring-green-500"
                                        onClick={() => setIsVerifyModalOpen(true)}
                                    >
                                        <span className="inline-block w-2 h-2 rounded-full bg-white/80 mr-1 animate-pulse" />
                                        Verify Customer
                                    </Button>
                                    <Link
                                        href="/restaurant-dashboard"
                                        className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-primary/90"
                                    >
                                        <svg className="w-4 h-4 opacity-80" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                            <rect x={3} y={3} width={18} height={18} rx={3} />
                                            <path d="M9 3v18M15 3v18" />
                                        </svg>
                                        Dashboard
                                    </Link>
                                </div>
                            ) : null}

                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={toggle}
                                className="rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                {isDarkMode ? (
                                    <Sun className="h-5 w-5 text-yellow-300" />
                                ) : (
                                    <Moon className="h-5 w-5 text-gray-700 dark:text-gray-200" />
                                )}
                            </Button>

                            {/* Bell icon for notifications */}
                            {isAuthenticated && <BellDropdown />}

                            {/* Auth / Profile (desktop) */}
                            {isAuthenticated ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <div className="flex items-center gap-2 cursor-pointer">
                                            <Avatar className="h-8 w-8">
                                                <AvatarFallback
                                                    className="bg-primary text-white font-semibold text-xs">
                                                    {getInitials(user?.username)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span
                                                className="hidden md:inline-block text-sm font-medium text-gray-800 dark:text-gray-200">
                                                {user?.username}
                                            </span>
                                        </div>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <DropdownMenuItem onSelect={() => navigate("/profile")}>
                                            <User size={16} className="mr-2 text-muted-foreground"/>
                                            Profile
                                        </DropdownMenuItem>
                                        {user?.is_restaurant_owner && (
                                            <DropdownMenuItem
                                                onSelect={() => navigate("/restaurant-dashboard")}>
                                                <LayoutDashboard size={16} className="mr-2 text-violet-700"/>
                                                Restaurant Dashboard
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem onSelect={() => navigate("/subscription")}>
                                            <Star size={16} className="mr-2 text-yellow-500"/>
                                            Subscription
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onSelect={() => navigate("/referral")}>
                                            <Gift size={16} className="mr-2 text-green-500"/>
                                            Refer & Earn
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator/>
                                        <DropdownMenuItem onSelect={logoutUser}>
                                            <LogOut size={16} className="mr-2 text-red-500"/>
                                            Sign out
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <div className="hidden md:flex items-center space-x-2">
                                    <Button variant="ghost" onClick={() => openAuthModal("login")}>
                                        Login
                                    </Button>
                                    <Button variant="default" onClick={() => openAuthModal("register")}>
                                        Register
                                    </Button>
                                </div>
                            )}

                            {/* Mobile menu button */}
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={toggleMobileMenu}
                                className="md:hidden"
                            >
                                {isMobileMenuOpen ? (
                                    <X className="h-6 w-6 text-gray-700 dark:text-gray-200"/>
                                ) : (
                                    <Menu className="h-6 w-6 text-gray-700 dark:text-gray-200"/>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Mobile menu */}
                    {isMobileMenuOpen && (
                        <div className="mt-4 space-y-4 md:hidden">
                            <div className="flex flex-col space-y-1">
                                {navLinks.map((link) => (
                                    <Link key={link.path} href={link.path}>
                                        <span
                                            className={`block px-3 py-2 rounded-md transition-colors ${
                                                location === link.path
                                                    ? "bg-gray-100 dark:bg-gray-700"
                                                    : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                            }`}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            {link.name}
                                        </span>
                                    </Link>
                                ))}
                            </div>

                            {/* Auth (mobile) */}
                            {!isAuthenticated ? (
                                <div className="flex flex-col space-y-2">
                                    <Button variant="outline" onClick={() => openAuthModal("login")}>
                                        Login
                                    </Button>
                                    <Button variant="default" onClick={() => openAuthModal("register")}>
                                        Register
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex flex-col space-y-2">
                                    <Button variant="ghost" onClick={() => navigate("/profile")}>
                                        Profile
                                    </Button>
                                    <Button variant="ghost" onClick={() => navigate("/subscription")}>
                                        Subscription
                                    </Button>
                                    <Button variant="ghost" onClick={() => navigate("/referral")}>
                                        Refer & Earn
                                    </Button>
                                    <Button variant="ghost" onClick={logoutUser}>
                                        Sign out
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </nav>

            <AuthModal
                isOpen={isAuthModalOpen}
                onClose={closeAuthModal}
                initialView={authType}
            />

            <VerifyCustomerModal
                isOpen={isVerifyModalOpen}
                onClose={() => setIsVerifyModalOpen(false)}
                onVerify={handleVerifyCustomer}
                isVerifying={isVerifying}
            />

            <CheckBookingVerificationModal
                open={showVerificationModal}
                booking={verificationBooking}
                onClose={() => {
                    setShowVerificationModal(false);
                    setVerificationBooking(null);
                }}
            />
        </>
    );
};

export default Header;
